import React, { useState } from 'react';
import { WorkspaceFile } from '@/models/file';
import styles from './document-view.module.scss';
import Button from '@/components/Button';
import LoadingSpinner from '@/components/LoadingSpinner';
import DownloadIcon from '@/assets/icons/download.svg?react';
import TrashIcon from '@/assets/icons/trash.svg?react';
import LibraryService from '@/services/library.service';
import useSession from '@/hooks/useSession';
import { toast } from 'react-toastify/unstyled';
import MarkdownPreview from '../MarkdownPreview';
import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { FilesPage, filesQueryKey } from '@/services/library.service';

interface Props {
  currentDoc?: WorkspaceFile;
  currentOrgID?: string;
  setCurrentDoc: React.Dispatch<
    React.SetStateAction<WorkspaceFile | undefined>
  >;
  isLoading: boolean;
  showContentsPreview: boolean;
  docContentRef: React.RefObject<HTMLDivElement>;
  enableFileManagement: boolean;
}

const DocumentView: React.FC<Props> = ({
  currentDoc,
  currentOrgID,
  setCurrentDoc,
  isLoading,
  showContentsPreview,
  docContentRef,
  enableFileManagement,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const { orgIndex, setSession } = useSession();
  const queryClient = useQueryClient();

  if (!currentDoc) {
    return (
      <div className={styles['document-view']}>
        <div className={styles['document-view__preview']}>
          {isLoading ? (
            <div className={styles['document-view__preview__loader']}>
              <p>Loading document...</p>
              <LoadingSpinner />
            </div>
          ) : (
            <div className={styles['document-view__preview__restriction']}>
              No document selected
            </div>
          )}
        </div>
      </div>
    );
  }

  const handleDelete = async () => {
    if (!currentDoc) {
      return;
    }
    try {
      const isConfirmed = confirm(
        `Are you sure you want to delete the document "${currentDoc.name}"? This action cannot be undone.`
      );

      if (isConfirmed === true) {
        setIsDeleting(true);
        const response = await LibraryService.deleteFile(
          currentDoc.id,
          currentOrgID
        );
        if (response) {
          setSession((prevSession) => ({
            ...prevSession,
            fileCount: prevSession.fileCount - 1,
          }));
          queryClient.setQueryData(
            filesQueryKey(orgIndex),
            (old: InfiniteData<FilesPage> | undefined) => {
              if (!old) return old;
              return {
                ...old,
                pages: old.pages.map((page) => ({
                  ...page,
                  files: page.files.filter((f) => f.id !== currentDoc.id),
                })),
              };
            }
          );
          setCurrentDoc(undefined);
        }
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'An error occurred.'
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className={styles['document-view']}>
      <div className={styles['document-view__metadata']}>
        <h3>{currentDoc.name}</h3>
        <p>
          <strong>Ingestion Date: </strong>
          {currentDoc.date.toLocaleString()}{' '}
          {currentDoc.sharepoint?.lastModified && (
            <i>
              (Last modified on Sharepoint:{' '}
              {new Date(currentDoc.sharepoint.lastModified).toLocaleString()})
            </i>
          )}
        </p>
        <hr />

        {currentDoc.sharepoint && (
          <div className={styles['document-view__metadata__location']}>
            {currentDoc.sharepoint.itemPath && (
              <p>
                <strong>Original file:</strong>{' '}
                <a
                  href={currentDoc.sharepoint.webUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {currentDoc.sharepoint.itemPath}
                </a>
              </p>
            )}
          </div>
        )}

        <div className={styles['document-view__metadata__actions']}>
          <Button
            variation="secondary"
            className={styles['document-view__download-btn']}
            onClick={() => {
              window.open(currentDoc.url, '_blank');
            }}
          >
            <DownloadIcon />
            Download
          </Button>
          {enableFileManagement && (
            <Button
              variation="secondary"
              className={styles['document-view__delete-btn']}
              data-type="delete"
              loading={isDeleting}
              onClick={handleDelete}
            >
              <TrashIcon />
              Delete
            </Button>
          )}
        </div>
      </div>

      <div
        className={styles['document-view__preview']}
        data-loading={isLoading}
      >
        {isLoading ? (
          <div className={styles['document-view__preview__loader']}>
            <p>Loading document...</p>
            <LoadingSpinner />
          </div>
        ) : ['pdf', 'txt'].includes(currentDoc.type) ? (
          <embed
            className={styles['document-view__preview__content']}
            src={currentDoc.url}
            type="application/pdf"
            title={`Preview of ${currentDoc.name}`}
          />
        ) : currentDoc.type === 'md' ? (
          <MarkdownPreview doc={currentDoc} />
        ) : (
          !showContentsPreview && (
            <div className={styles['document-view__preview__restriction']}>
              Unable to preview this document type.
            </div>
          )
        )}

        <div
          className={styles['document-view__preview__content']}
          data-extension={currentDoc.type}
          ref={docContentRef}
          style={{
            display: ['csv', 'doc', 'docx'].includes(currentDoc.type)
              ? 'block'
              : 'none',
          }}
        />
      </div>
    </div>
  );
};

export default DocumentView;
